import { Link, router } from "@inertiajs/react"
import {
  BookOpen,
  Calendar,
  GraduationCap,
  Loader2,
  Lock,
  Mic,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import AudioPlayer from "@/components/conversation/audio-player"
import { AudioUploader } from "@/components/editorial/AudioUploader"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { jsonFetch } from "@/lib/fetch-utils"
import { getInitials, timeAgo } from "@/lib/format-utils"
import { cn } from "@/lib/utils"
import type { Comment } from "@/types/phrase"

interface PresignResponse {
  url: string
  key: string
  expires_in: number
}

async function uploadAudioToR2(file: File): Promise<string> {
  const presign = await jsonFetch<PresignResponse>("/r2/presign_upload", {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type,
      size: file.size,
    }),
  })
  const putRes = await fetch(presign.url, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  })
  if (!putRes.ok) throw new Error("Failed to upload audio")
  return presign.key
}

function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const audio = new Audio()
    const url = URL.createObjectURL(file)
    audio.addEventListener("loadedmetadata", () => {
      const duration = isFinite(audio.duration) ? audio.duration : 0
      URL.revokeObjectURL(url)
      resolve(Math.round(duration))
    })
    audio.addEventListener("error", () => {
      URL.revokeObjectURL(url)
      resolve(0)
    })
    audio.src = url
  })
}

interface CommentCardProps {
  comment: Comment
  phraseSlug: string
  isAuthenticated?: boolean
  className?: string
}

export function CommentCard({ comment, phraseSlug, isAuthenticated = false, className }: CommentCardProps) {
  const isTeacher = comment.user.role === "teacher"

  // Edit state
  const [editing, setEditing] = useState(false)
  const [editBody, setEditBody] = useState(comment.body)
  const [editAudioFile, setEditAudioFile] = useState<File | null>(null)
  const [removeExistingAudio, setRemoveExistingAudio] = useState(false)
  const [showAudioUploader, setShowAudioUploader] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (editing && textareaRef.current) {
      const el = textareaRef.current
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }
  }, [editing])

  const handleStartEdit = useCallback(() => {
    setEditBody(comment.body)
    setEditAudioFile(null)
    setRemoveExistingAudio(false)
    setShowAudioUploader(false)
    setError(null)
    setEditing(true)
  }, [comment.body])

  const handleCancelEdit = useCallback(() => {
    setEditing(false)
    setEditBody(comment.body)
    setEditAudioFile(null)
    setRemoveExistingAudio(false)
    setShowAudioUploader(false)
    setError(null)
  }, [comment.body])

  const handleSaveEdit = useCallback(async () => {
    const trimmed = editBody.trim()
    const bodyChanged = trimmed !== comment.body
    const audioChanged = editAudioFile !== null || removeExistingAudio

    if (!trimmed && !editAudioFile && !comment.audioUrl) {
      handleCancelEdit()
      return
    }
    if (!bodyChanged && !audioChanged) {
      handleCancelEdit()
      return
    }

    setSaving(true)
    setError(null)

    try {
      const payload: Record<string, unknown> = {}
      if (bodyChanged) payload.body = trimmed

      if (editAudioFile) {
        const [audioR2Key, durationSeconds] = await Promise.all([
          uploadAudioToR2(editAudioFile),
          getAudioDuration(editAudioFile),
        ])
        payload.audio_r2_key = audioR2Key
        if (durationSeconds > 0) payload.duration_seconds = durationSeconds
      } else if (removeExistingAudio) {
        payload.remove_audio = true
      }

      await jsonFetch(`/phrases/${phraseSlug}/comments/${comment.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      })
      setEditing(false)
      router.reload({ preserveScroll: true })
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      }
    } finally {
      setSaving(false)
    }
  }, [editBody, editAudioFile, removeExistingAudio, comment.body, comment.audioUrl, comment.id, phraseSlug, handleCancelEdit])

  const handleDelete = useCallback(async () => {
    setDeleting(true)
    try {
      await jsonFetch(`/phrases/${phraseSlug}/comments/${comment.id}`, {
        method: "DELETE",
      })
      setShowDeleteDialog(false)
      router.reload({ preserveScroll: true })
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      }
      setDeleting(false)
    }
  }, [comment.id, phraseSlug])

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancelEdit()
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        void handleSaveEdit()
      }
    },
    [handleCancelEdit, handleSaveEdit],
  )

  return (
    <div
      className={cn(
        "group rounded-xl border p-4 transition-colors sm:p-5",
        isTeacher
          ? "border-indigo-200/60 bg-indigo-50/30 dark:border-indigo-800/40 dark:bg-indigo-950/20"
          : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
        className,
      )}
    >
      <div className="flex gap-4">
        {/* Left accent bar for teachers */}
        {isTeacher && (
          <div className="hidden w-1 shrink-0 self-stretch rounded-full bg-gradient-to-b from-indigo-400 to-violet-500 sm:block" />
        )}

        <div className="min-w-0 flex-1 space-y-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            {/* Left: avatar + name + badge + timestamp */}
            <div className="flex min-w-0 items-center gap-3">
              {/* Avatar with gradient ring for teachers */}
              <div
                className={cn(
                  "shrink-0 rounded-full",
                  isTeacher && "bg-gradient-to-br from-indigo-400 to-violet-500 p-[2px]",
                )}
              >
                <Avatar
                  className={cn(
                    "size-9",
                    isTeacher && "border-2 border-white dark:border-slate-900",
                  )}
                >
                  <AvatarFallback
                    className={cn(
                      "text-xs font-semibold",
                      isTeacher
                        ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
                    )}
                  >
                    {getInitials(comment.user.name)}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                    {comment.user.name}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="shrink-0">
                        {isTeacher ? (
                          <GraduationCap className="size-4 text-indigo-600 dark:text-indigo-400" />
                        ) : (
                          <BookOpen className="size-3.5 text-slate-400 dark:text-slate-500" />
                        )}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isTeacher ? "Teacher" : "Learner"}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {timeAgo(comment.createdAt)}
                </span>
              </div>
            </div>

            {/* Right: actions */}
            <div className="flex shrink-0 items-center gap-2">
              {/* Book a Class CTA for teachers */}
              {isTeacher && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      disabled
                      className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-medium text-indigo-600 opacity-70 transition-opacity hover:opacity-100 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-400"
                    >
                      <Calendar className="size-3.5" />
                      <span className="hidden sm:inline">Book a Class</span>
                      <span className="sm:hidden">Book</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Coming soon &mdash; calendar integration in progress
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Author actions dropdown */}
              {comment.canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                    >
                      <MoreHorizontal className="size-4 text-slate-400" />
                      <span className="sr-only">Comment actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-36">
                    <DropdownMenuItem onClick={handleStartEdit}>
                      <Pencil className="mr-2 size-3.5" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                    >
                      <Trash2 className="mr-2 size-3.5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Body — edit mode or display */}
          {editing ? (
            <div className="space-y-2">
              <Textarea
                ref={textareaRef}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                onKeyDown={handleEditKeyDown}
                maxLength={1000}
                rows={3}
                disabled={saving}
                className="resize-none border-slate-200 bg-white text-sm placeholder:text-slate-400 focus-visible:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900"
              />

              {/* Audio uploader in edit mode */}
              {showAudioUploader && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      Record or upload audio
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      onClick={() => {
                        setShowAudioUploader(false)
                        setEditAudioFile(null)
                        setRemoveExistingAudio(false)
                      }}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                  <AudioUploader
                    value={editAudioFile}
                    existingUrl={!removeExistingAudio ? comment.audioUrl : undefined}
                    onChange={setEditAudioFile}
                    onRemoveExisting={() => setRemoveExistingAudio(true)}
                    maxSizeMb={5}
                  />
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">
                    Esc to cancel &middot; {navigator.platform?.includes("Mac") ? "Cmd" : "Ctrl"}+Enter to save
                  </span>
                  {!showAudioUploader && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAudioUploader(true)}
                      className="gap-1.5 text-xs text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400"
                    >
                      <Mic className="size-3.5" />
                      {comment.audioUrl ? "Change audio" : "Add audio"}
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void handleSaveEdit()}
                    disabled={
                      saving ||
                      (!editBody.trim() && !editAudioFile && !comment.audioUrl) ||
                      (editBody.trim() === comment.body && !editAudioFile && !removeExistingAudio)
                    }
                    className="gap-1.5 bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    {saving && <Loader2 className="size-3.5 animate-spin" />}
                    Save
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                {comment.body}
              </p>

              {/* Audio player — gated for logged-in users */}
              {comment.audioUrl && isAuthenticated && (
                <div className="max-w-sm">
                  <AudioPlayer
                    audioUrl={comment.audioUrl}
                    variant="dark"
                    className="rounded-lg bg-slate-50 px-2 py-1 dark:bg-slate-800/50"
                  />
                </div>
              )}
              {comment.hasAudio && !isAuthenticated && (
                <div className="relative max-w-sm select-none overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                  <div className="pointer-events-none opacity-40 blur-[0.5px] grayscale">
                    <AudioPlayer
                      audioUrl=""
                      variant="dark"
                      className="rounded-lg bg-slate-100 px-2 py-1 dark:bg-slate-800"
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-50/50 dark:bg-slate-900/50">
                    <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 shadow-sm dark:border-slate-600 dark:bg-slate-800">
                      <Lock className="size-3 shrink-0 text-slate-400 dark:text-slate-500" />
                      <p className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
                        <Link
                          href="/sign_in"
                          className="text-indigo-600 hover:underline dark:text-indigo-400"
                        >
                          Log in
                        </Link>
                        {" or "}
                        <Link
                          href="/sign_up"
                          className="text-indigo-600 hover:underline dark:text-indigo-400"
                        >
                          sign up
                        </Link>
                        {" to listen to audio comments"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete comment?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Your comment will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleDelete()
              }}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting && <Loader2 className="mr-2 size-3.5 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
